import React, { memo, useEffect, useMemo, useState } from 'react';

import Table from '@components/common/Table';
import {
	getColumns,
	getDynamicColumns,
} from '@components/routes/CmModule/ConfigurationComparison/BranchTable/manageMoBranch';

import { useMutateExportMoBranch } from '@hooks/cmModule/useMutateExportMoBranch';
import { useQueriesOptionsDetailsTable } from '@hooks/cmModule/useQueriesOptionsDetailsTable';
import { useQueryConfigIdsBranch } from '@hooks/cmModule/useQueryConfigIdsBranch';
import { useQueryDeltasBranch } from '@hooks/cmModule/useQueryDeltasBranch';
import { useQueryGnbIdsBranch } from '@hooks/cmModule/useQueryGnbIdsBranch';
import { useQueryMoBranch } from '@hooks/cmModule/useQueryMoBranch';
import { UseQueryMoTreeColumnNames } from '@hooks/cmModule/useQueryMoTreeColumnNames';
import { useQueryParametersBranch } from '@hooks/cmModule/useQueryParametersBranch';
import { useQuerySiteIdsBranch } from '@hooks/cmModule/useQuerySiteIdsBranch';

import { moBranch as moBranchSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setConfigId,
	setDelta,
	setNodeId,
	setPage,
	setPageSize,
	setParamName,
	setSiteId,
	setSort,
} from '@slices/moBranch';

import { generateFilters } from '@utils/cmModule';
import { setFilterUtil } from '@utils/filterUtil';

import { IManagedObjects } from '@interfaces/cmModule/cmModule';
import { IBranchTable } from '@interfaces/cmModule/configurationComparison';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { MIN_FILTER_WIDTH } from './constants';

const TABLE_SIZE = 'calc(80vh + 80px)';

interface IBranchTableProps {
	selectedTableBranch: IBranchTable;
	isMavenirVendor?: boolean;
}

const BranchTable = ({
	selectedTableBranch,
	isMavenirVendor,
}: IBranchTableProps) => {
	const dispatch = useAppDispatch();

	const [selectedDelta, setSelectedDelta] = useState<TSelectOption | null>(null);
	const [selectedSiteId, setSelectedSiteId] = useState<TSelectOption | null>(null);
	const [selectedNodeId, setSelectedNodeId] = useState<TSelectOption | null>(null);
	const [selectedConfigId, setSelectedConfigId] = useState<TSelectOption<string> | null>(null);
	const [selectedParamName, setSelectedParamName] = useState<TSelectOption | null>(null);
	const [selectedGeneratedFilters, setSelectedGeneratedFilters] = useState<Record<string, TSelectOption>>({});
	const [searchValue, setSearchValue] = useState('');

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		delta,
		configId,
		siteId,
		gnbId,
		paramName,
	} = useAppSelector(moBranchSelector);

	const { deltasOptions, isLoadingDeltas } = useQueryDeltasBranch(selectedTableBranch.id);
	const { siteIdsOptions, isLoadingSiteIds } = useQuerySiteIdsBranch(selectedTableBranch.id);
	const { gnbIdsOptions, isLoadingGnbIds } = useQueryGnbIdsBranch(selectedTableBranch.id);
	const { configIdsOptions, isLoadingConfigIds } = useQueryConfigIdsBranch(selectedTableBranch.id);
	const { parametersOptions, isLoadingParameters } = useQueryParametersBranch(selectedTableBranch.id);

	const managedObjects = useMemo(() => {
		return Object.entries(selectedGeneratedFilters).reduce<IManagedObjects[]>((acc, [key, option]) => {
			const moItem = option?.value ? { moName: key, moId: option.value.toString() } : null;

			return moItem
				? [...acc, moItem]
				: acc;
		}, []);
	}, [selectedGeneratedFilters]);

	const { exportBranch } = useMutateExportMoBranch({
		comparisonId: selectedTableBranch.id,
		delta,
		siteId,
		gnbId,
		paramName,
		managedObjects,
	});

	const {
		branch,
		isLoadingBranch: isLoadingMoBranch,
		dataSourceCreatedAtSecond,
		dataSourceCreatedAtFirst,
		countOfRecords,
	} = useQueryMoBranch({
		comparisonId: selectedTableBranch.id,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		delta,
		configId,
		siteId,
		gnbId,
		paramName,
		managedObjects,
		searchValue,
	});

	const { moNames, isLoadingMoNames } = UseQueryMoTreeColumnNames({
		comparisonId: selectedTableBranch.id,
		isMavenirVendor,
	});

	const { detailsOptions, isInitialDetailsOptionsLoading } = useQueriesOptionsDetailsTable({
		comparisonId: selectedTableBranch.id,
		moNames,
	});

	const isLoadingBranch = (
		isLoadingMoBranch ||
		isLoadingDeltas ||
		isLoadingSiteIds ||
		isLoadingGnbIds ||
		isLoadingConfigIds ||
		isLoadingParameters ||
		isInitialDetailsOptionsLoading ||
		isLoadingMoNames
	);

	const generatedFilters: Array<TTableFilter> = generateFilters(
		detailsOptions,
		selectedGeneratedFilters,
		setSelectedGeneratedFilters,
	);

	const vendorFilters = useMemo(() => {
		return isMavenirVendor
			? [
				{
					name: 'gnbId',
					placeholder: 'Node ID',
					option: selectedNodeId,
					options: gnbIdsOptions,
					minWidth: MIN_FILTER_WIDTH,
					setState: setSelectedNodeId,
					defaultValue: gnbId,
				},
				{
					name: 'configId',
					placeholder: 'CNF ID',
					option: selectedConfigId,
					options: configIdsOptions,
					minWidth: MIN_FILTER_WIDTH,
					setState: setSelectedConfigId,
					defaultValue: 'configId',
				},
			] : [
				{
					name: 'paramName',
					placeholder: 'Parameter',
					option: selectedParamName,
					options: parametersOptions,
					minWidth: MIN_FILTER_WIDTH,
					setState: setSelectedParamName,
					defaultValue: paramName,
				},
				{
					name: 'siteId',
					placeholder: 'Site ID',
					option: selectedSiteId,
					options: siteIdsOptions,
					minWidth: MIN_FILTER_WIDTH,
					setState: setSelectedSiteId,
					defaultValue: 'siteId',
				},
			];
	}, [
		isMavenirVendor,
		gnbId,
		parametersOptions,
		paramName,
		configIdsOptions,
		gnbIdsOptions,
		selectedNodeId,
		selectedConfigId,
		selectedParamName,
		selectedSiteId,
		siteIdsOptions,
	]);

	const filters: Array<TTableFilter> = useMemo(() => (
		[
			...vendorFilters,
			{
				name: 'delta',
				placeholder: 'Delta',
				option: selectedDelta,
				options: deltasOptions,
				minWidth: MIN_FILTER_WIDTH,
				setState: setSelectedDelta,
				defaultValue: delta,
			},
			...generatedFilters,
		]
	), [
		delta,
		deltasOptions,
		generatedFilters,
		selectedDelta,
		vendorFilters,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedParamName,
			currentValue: paramName,
			setAction: setParamName,
			fieldName: 'paramName',
			dispatch,
		});
	}, [dispatch, paramName, selectedParamName]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedSiteId,
			currentValue: siteId,
			setAction: setSiteId,
			fieldName: 'siteId',
			dispatch,
		});
	}, [dispatch, selectedSiteId, siteId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedNodeId,
			currentValue: gnbId,
			setAction: setNodeId,
			fieldName: 'gnbId',
			dispatch,
		});
	}, [dispatch, selectedNodeId, gnbId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedDelta,
			currentValue: delta,
			setAction: setDelta,
			fieldName: 'delta',
			dispatch,
		});
	}, [delta, dispatch, selectedDelta]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedConfigId,
			currentValue: configId,
			setAction: setConfigId,
			fieldName: 'configId',
			dispatch,
		});
	}, [configId, dispatch, selectedConfigId]);

	const columns = useMemo(() => getColumns({
		dataSourceCreatedAtFirst,
		dataSourceCreatedAtSecond,
		isMavenirVendor,
	}), [dataSourceCreatedAtFirst, dataSourceCreatedAtSecond, isMavenirVendor]);

	const dynamicColumns = useMemo(() => getDynamicColumns(moNames), [moNames]);

	const branchColumns = useMemo(() => {
		return dynamicColumns ? [...columns, ...dynamicColumns] : columns;
	}, [columns, dynamicColumns]);

	const pageHandler = (page: number) => dispatch(setPage({ page }));

	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const handleSort = (sortBy: Array<ISortBy>) =>
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const handleExport = () => {
		exportBranch();
	};

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: '',
		name: 'name',
		handler: (searchValue: string) => setSearchValue(searchValue),
		tooltipText: 'Search by parameter and site ID',
	}], [setSearchValue]);

	return (
		<Table
			columns={branchColumns}
			data={branch}
			exportTooltipText='Export branch to file'
			filters={filters}
			handleCustomExport={handleExport}
			isExport
			isLoading={isLoadingBranch}
			isPinColumns={isMavenirVendor}
			isWithoutActions
			isWithoutCheckbox
			isWithoutDeleteAction
			maxHeight={TABLE_SIZE}
			pagination={pagination}
			search={search}
			searchThreshold={0}
			sort={sort}
			stickyLeftColumnIndexes={[0, 1, 2]}
		/>
	);
};

export default memo(BranchTable);
